import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

export type ToastType = 'info' | 'success' | 'error' | 'warning';

export interface ToastOptions {
  type?: ToastType;
  durationMs?: number;
}

interface ToastItem {
  id: number;
  message: string;
  type: ToastType;
}

interface ToastContextValue {
  showToast: (message: string, options?: ToastOptions) => void;
}

// Defensive default: if useToast() is somehow called outside a provider it
// no-ops rather than crashing the app (medical tool — fail safe, not loud).
const ToastContext = createContext<ToastContextValue>({ showToast: () => {} });

export const useToast = () => useContext(ToastContext);

const typeStyles: Record<ToastType, string> = {
  info: 'bg-surface-raised border-accent-500/40 text-content',
  success: 'bg-surface-raised border-green-500/50 text-content',
  error: 'bg-surface-raised border-red-500/50 text-content',
  warning: 'bg-surface-raised border-amber-500/50 text-content',
};

const typeIcon: Record<ToastType, string> = {
  info: 'ℹ️',
  success: '✓',
  error: '⚠️',
  warning: '⚠️',
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const remove = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const showToast = useCallback((message: string, options?: ToastOptions) => {
    const id = nextId.current++;
    const type = options?.type ?? 'info';
    const durationMs = options?.durationMs ?? (type === 'error' ? 7000 : 4000);
    setToasts(prev => [...prev, { id, message, type }]);
    if (durationMs > 0) {
      setTimeout(() => remove(id), durationMs);
    }
  }, [remove]);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div
        className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 max-w-sm w-[calc(100%-2rem)] sm:w-auto"
        aria-live="polite"
        aria-atomic="false"
      >
        {toasts.map(t => (
          <div
            key={t.id}
            role="status"
            className={`flex items-start gap-2 rounded-lg border px-4 py-3 shadow-lg text-sm animate-slide-up ${typeStyles[t.type]}`}
          >
            <span aria-hidden="true" className="mt-0.5">{typeIcon[t.type]}</span>
            <span className="flex-1">{t.message}</span>
            <button
              onClick={() => remove(t.id)}
              aria-label="Dismiss notification"
              className="text-content-tertiary hover:text-content transition-colors"
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};
